import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import request from "@/lib/api/handler";
import type { PayloadAction } from "@reduxjs/toolkit";
import networkErrorHandler from "../../utils/networkErrorHandler";
import { UserType } from "../../types";
import {
  claimCompanyProfile,
  CompanyUserAccountType,
} from "./companyProfileSlice";

export interface CompanyAccount {
  companyId: string;
  accountType: CompanyUserAccountType;
}

export interface User {
  id: string;
  name?: string;
  emails: string[];
  profilePictureLink?: string;
  onboarded?: boolean;
  userType: UserType[];
  companyAccounts: CompanyAccount[];
  marketingConsent: boolean;
  tosVersionAgreement?: string;
  candidateId?: string;
  biddingEnabled?: boolean;
}

// Define a type for the slice state
interface AuthState {
  user?: User;
  isAuthenticated: boolean;
  status: "idle" | "pending" | "succeeded" | "failed";
  postingStatus: "idle" | "posting" | "succeeded" | "failed";
}

// Define the initial state using that type
const initialState: AuthState = {
  isAuthenticated: false,
  status: "idle",
  postingStatus: "idle",
};

export const completeOnboarding = createAsyncThunk(
  "auth/onboard",
  async (token: string, { rejectWithValue }) => {
    try {
      return request<User>({
        method: "POST",
        path: "/api/user/onboard",
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    login: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = undefined;
      state.isAuthenticated = false;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(completeOnboarding.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        completeOnboarding.fulfilled,
        (state, action: PayloadAction<User | undefined>) => {
          state.status = "succeeded";
          state.user = action?.payload;
        },
      )
      .addCase(completeOnboarding.rejected, (state) => {
        state.status = "failed";
      })
      // When a company is claimed, the user will be updated to reflect their membership in that team
      .addCase(
        claimCompanyProfile.fulfilled,
        (state, action: PayloadAction<User | undefined>) => {
          state.user = action?.payload;
        },
      );
  },
});

export const { login, logout, setUser } = authSlice.actions;

export default authSlice.reducer;
